/**
 * Hover - Infinite scroll for collection pages.
 * This script is loaded only when pagination buttons are disabled via
 * section.settings.show_pagination = false (hover-main-collection section).
**/
class ProductInfiniteScroll extends HTMLElement {
  constructor() {
    super();

    // find the product list container
    this.productList = this.querySelector('product-list');
    this.innerContainer = this.productList?.querySelector('.product-list__inner');
    
    if (!this.innerContainer) {
      console.warn('ProductInfiniteScroll: No product list container found');
      return;
    }

    // decide current page from url (default to 1)
    const params = new URLSearchParams(window.location.search);
    this.currentPage = parseInt(params.get('page') || '1', 10);

    this.isLoading = false;
    this.hasMore = true;

    // create element that triggers loading next page
    this.observerTrigger = document.createElement('div');
    this.observerTrigger.className = 'product-infinite-scroll__trigger';
    this.observerTrigger.style.cssText = 'height: 1px; width: 100%; pointer-events: none;';
    
    // insert the trigger after the product list
    this.productList.insertAdjacentElement('afterend', this.observerTrigger);

    // observe when sentinel is near viewport
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this.isLoading && this.hasMore) {
            this.loadNextPage();
          }
        });
      },
      {
        rootMargin: '500px',
      }
    );

    this.observer.observe(this.observerTrigger);
    console.log('ProductInfiniteScroll initialized for page:', this.currentPage);
  }

  // build url for next page
  buildNextPageUrl() {
    const url = new URL(window.location.href);
    const nextPage = this.currentPage + 1;
    url.searchParams.set('page', nextPage.toString());
    return url.toString();
  }

  // fetch next page of products and append them to the list
  async loadNextPage() {
    if (this.isLoading || !this.hasMore) return;
    
    this.isLoading = true;
    console.log('Loading page:', this.currentPage + 1);

    try {
      const response = await fetch(this.buildNextPageUrl(), {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch next page: ${response.status}`);
      }

      const htmlText = await response.text();
      const doc = new DOMParser().parseFromString(htmlText, 'text/html');
      const nextProductList = doc.querySelector('product-list .product-list__inner');

      // if no additional products, stop the observing
      if (!nextProductList || nextProductList.children.length === 0) {
        console.log('No more products to load');
        this.hasMore = false;
        this.cleanup();
        return;
      }

      // appends the new products we got to the list (from the next page)
      const newProducts = Array.from(nextProductList.children);
      console.log('Appending', newProducts.length, 'new products');
      
      newProducts.forEach((item) => {
        this.innerContainer.appendChild(item);
      });

      // update current page (for the next request)
      this.currentPage += 1;

    } catch (error) {
      console.error('[ProductInfiniteScroll]', error);
      this.hasMore = false;
      this.cleanup();
    } finally {
      this.isLoading = false;
    }
  }

  cleanup() {
    if (this.observer) {
      this.observer.disconnect();
    }
    if (this.observerTrigger && this.observerTrigger.parentNode) {
      this.observerTrigger.parentNode.removeChild(this.observerTrigger);
    }
  }

  disconnectedCallback() {
    this.cleanup();
  }
}

if (!customElements.get('product-infinite-scroll')) {
  customElements.define('product-infinite-scroll', ProductInfiniteScroll);
}
